<script setup lang="ts">
import { computed } from 'vue'

import type { StepperProps, StepperValue } from './types'

import { Icon } from '@/shared/ui/icon'

const props = withDefaults(defineProps<StepperProps>(), {
  compact: false,
})
const model = defineModel<StepperValue>({ required: true })

type StepState = 'complete' | 'current' | 'upcoming'

const currentIndex = computed(() => props.steps.findIndex((step) => step.value === model.value))

function stateAt(index: number): StepState {
  if (currentIndex.value < 0) return 'upcoming'
  if (index < currentIndex.value) return 'complete'
  return index === currentIndex.value ? 'current' : 'upcoming'
}

function select(value: StepperValue, disabled = false): void {
  if (!disabled && value !== model.value) model.value = value
}

function stepKey(value: StepperValue): string {
  return `${typeof value}:${String(value)}`
}
</script>

<template>
  <nav
    class="ui-stepper w-full font-sans"
    :class="{ 'ui-stepper--compact': compact }"
    :aria-label="ariaLabel"
  >
    <ol class="ui-stepper__list m-0 flex w-full list-none items-start overflow-x-auto p-0">
      <li
        v-for="(step, index) in steps"
        :key="stepKey(step.value)"
        class="ui-stepper__step relative min-w-0 flex-1 basis-20 sm:basis-[5.5rem]"
        :class="[
          `ui-stepper__step--${stateAt(index)}`,
          { 'ui-stepper__step--disabled': step.disabled },
          { 'basis-[4.5rem]': compact },
        ]"
        :data-state="stateAt(index)"
      >
        <span
          v-if="index < steps.length - 1"
          class="ui-stepper__connector absolute z-base h-0.5 bg-border"
          :class="[
            compact
              ? 'start-[calc(50%_+_var(--ui-space-sm))] end-[calc(-50%_+_var(--ui-space-sm))] top-[calc(var(--ui-space-sm)_-_1px)]'
              : 'start-[calc(50%_+_var(--ui-space-md))] end-[calc(-50%_+_var(--ui-space-md))] top-[calc(var(--ui-space-md)_-_1px)]',
            { 'bg-success': stateAt(index) === 'complete' },
          ]"
          aria-hidden="true"
        />
        <button
          class="ui-stepper__button relative z-raised flex w-full cursor-pointer flex-col items-center bg-transparent text-muted [font:inherit] focus-visible:rounded-sm focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-2xs focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
          :class="compact ? 'gap-2xs px-2xs' : 'gap-xs px-xs'"
          type="button"
          :disabled="step.disabled"
          :aria-disabled="step.disabled ? 'true' : undefined"
          :aria-current="stateAt(index) === 'current' ? 'step' : undefined"
          @click="select(step.value, step.disabled)"
        >
          <span
            class="ui-stepper__marker grid place-items-center rounded-full border-2 border-border bg-surface font-semibold leading-none text-muted"
            :class="[
              compact ? 'h-lg w-lg text-[0.6875rem]' : 'h-xl w-xl text-xs',
              {
                'border-primary bg-primary text-primary-fg': stateAt(index) === 'current',
                'border-success bg-success text-surface': stateAt(index) === 'complete',
              },
            ]"
            aria-hidden="true"
          >
            <Icon v-if="stateAt(index) === 'complete'" name="check" :size="compact ? 12 : 14" />
            <span v-else>{{ index + 1 }}</span>
          </span>
          <span
            class="ui-stepper__label max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-center text-xs font-medium leading-tight"
            :class="{
              'font-semibold text-fg': stateAt(index) === 'current',
              'text-success': stateAt(index) === 'complete',
            }"
          >
            {{ step.label }}
          </span>
        </button>
      </li>
    </ol>
  </nav>
</template>
